/**
 * @fileoverview 服务器连接状态守卫
 * @description 全局监测后端 API 可达性。
 *   - 每 10 秒轮询一次 /api/v1/market/marketList?page=0（公开接口）
 *   - 连续 2 次失败 → 显示全屏遮罩（「连接失败」或「维护中」）
 *   - 恢复后自动隐藏遮罩
 *   - 用户可手动点击「重新尝试」立即发起一次检测
 *   - 支持 i18n 多语言（zh / en）
 * 设计风格：深色毛玻璃遮罩，品牌黄色强调，与整体 Dark 主题一致
 * @module components/ServerStatusGuard
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../services/api';

// 轮询间隔（毫秒）
const POLL_INTERVAL = 10_000;
// 连续失败次数阈值，超过此值才显示遮罩
const FAIL_THRESHOLD = 2;
// 单次请求超时（毫秒）
const REQUEST_TIMEOUT = 6_000;

type ServerState = 'ok' | 'error' | 'maintenance';

/**
 * 发起一次健康检查请求
 * 使用 /api/v1/market/marketList?page=0 作为探针（公开接口，无需登录）
 */
async function checkServer(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  try {
    const url = `${API_BASE_URL}/api/v1/market/marketList?page=0`;
    const res = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    return res.ok || res.status < 500;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export default function ServerStatusGuard() {
  const { t } = useTranslation();
  const [serverState, setServerState] = useState<ServerState>('ok');
  const [isRetrying, setIsRetrying] = useState(false);
  const failCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runCheck = useCallback(async () => {
    const ok = await checkServer();
    if (ok) {
      failCountRef.current = 0;
      setServerState('ok');
    } else {
      failCountRef.current += 1;
      if (failCountRef.current >= FAIL_THRESHOLD) {
        // 超过 6 次（约 1 分钟）认为是计划维护
        setServerState(failCountRef.current >= 6 ? 'maintenance' : 'error');
      }
    }
  }, []);

  // 启动轮询
  useEffect(() => {
    // 页面加载后延迟 3 秒再开始首次检测，避免影响首屏渲染
    const initTimer = setTimeout(() => {
      runCheck();
      timerRef.current = setInterval(runCheck, POLL_INTERVAL);
    }, 3000);

    return () => {
      clearTimeout(initTimer);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [runCheck]);

  // 手动重试
  const handleRetry = async () => {
    setIsRetrying(true);
    const ok = await checkServer();
    if (ok) {
      failCountRef.current = 0;
      setServerState('ok');
    } else {
      failCountRef.current += 1;
      setServerState(failCountRef.current >= 6 ? 'maintenance' : 'error');
    }
    setIsRetrying(false);
  };

  // 服务器正常时不渲染任何内容
  if (serverState === 'ok') return null;

  const isMaintenance = serverState === 'maintenance';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        background: 'rgba(11, 14, 17, 0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {/* 背景装饰光晕 */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isMaintenance
            ? 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(240,185,11,0.06) 0%, transparent 70%)'
            : 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(246,70,93,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative flex flex-col items-center gap-6 px-8 py-10 max-w-sm w-full mx-4">
        {/* 图标区域 */}
        <div className="relative">
          {/* 外圈脉冲动画 */}
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              background: isMaintenance
                ? 'rgba(240,185,11,0.15)'
                : 'rgba(246,70,93,0.15)',
              animationDuration: '2s',
            }}
          />
          <div
            className="relative w-20 h-20 rounded-full flex items-center justify-center"
            style={{
              background: isMaintenance
                ? 'rgba(240,185,11,0.12)'
                : 'rgba(246,70,93,0.12)',
              border: `1px solid ${isMaintenance ? 'rgba(240,185,11,0.3)' : 'rgba(246,70,93,0.3)'}`,
            }}
          >
            {isMaintenance ? (
              // 维护中图标：扳手
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F0B90B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            ) : (
              // 连接失败图标：断开的链接
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F6465D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                <line x1="2" y1="2" x2="22" y2="22"/>
              </svg>
            )}
          </div>
        </div>

        {/* 文字区域 */}
        <div className="text-center space-y-2">
          <h2
            className="text-xl font-bold"
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              color: isMaintenance ? '#F0B90B' : '#F6465D',
            }}
          >
            {isMaintenance
              ? t('serverStatus.maintenance')
              : t('serverStatus.connectionFailed')}
          </h2>
          <p
            className="text-sm leading-relaxed whitespace-pre-line"
            style={{ color: 'rgba(234,236,239,0.6)' }}
          >
            {isMaintenance
              ? t('serverStatus.maintenanceDesc')
              : t('serverStatus.connectionFailedDesc')}
          </p>
        </div>

        {/* 状态指示器 */}
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(234,236,239,0.5)',
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: isMaintenance ? '#F0B90B' : '#F6465D',
              boxShadow: `0 0 6px ${isMaintenance ? '#F0B90B' : '#F6465D'}`,
            }}
          />
          {isMaintenance
            ? t('serverStatus.maintenanceMode')
            : t('serverStatus.unreachable')}
        </div>

        {/* 操作按钮（仅连接失败时显示） */}
        {!isMaintenance && (
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: isRetrying
                ? 'rgba(240,185,11,0.15)'
                : 'linear-gradient(135deg, #F0B90B 0%, #e6a800 100%)',
              color: isRetrying ? '#F0B90B' : '#0B0E11',
              border: isRetrying ? '1px solid rgba(240,185,11,0.3)' : 'none',
              fontFamily: 'Space Grotesk, sans-serif',
            }}
          >
            {isRetrying ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                {t('serverStatus.retrying')}
              </span>
            ) : (
              t('serverStatus.retry')
            )}
          </button>
        )}

        {/* 维护中时显示自动检测提示 */}
        {isMaintenance && (
          <p className="text-xs text-center" style={{ color: 'rgba(234,236,239,0.35)' }}>
            {t('serverStatus.autoCheck')}
          </p>
        )}

        {/* 底部品牌标识 */}
        <div className="flex items-center gap-1.5 mt-2">
          <div
            className="w-5 h-5 rounded flex items-center justify-center text-xs font-bold"
            style={{ background: '#F0B90B', color: '#0B0E11' }}
          >
            A
          </div>
          <span className="text-xs font-medium" style={{ color: 'rgba(234,236,239,0.4)', fontFamily: 'Space Grotesk, sans-serif' }}>
            AugurX
          </span>
        </div>
      </div>
    </div>
  );
}
